import axios, { InternalAxiosRequestConfig } from 'axios';
import { Contract, ContractSearch, ContractUpdate, AgreementMilestone } from '../types/contract';
import { User } from '../types/user';
import { getApiUrl, API_ENDPOINTS, DEFAULT_COMPANY } from '../environments';
import { getStoredToken } from './auth';

interface ApiResponse<T> {
    success: boolean;
    data: T | string; // data puede ser del tipo T o un string (mensaje de error)
}

// Interceptor para añadir el token y loguear las peticiones
const addAuthAndLog = async (config: InternalAxiosRequestConfig) => {
    const token = await getStoredToken();
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('🚀 Request:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        data: config.data
    });
    return config;
};

const logResponse = (response: any) => {
    console.log('✅ Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
    });
    return response;
};

const logError = async (error: any) => {
    console.log('❌ Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
    });
    if (error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
    }
    return Promise.reject(error);
};

export const getContract = async (searchParams: ContractSearch) => {
    const instance = axios.create();
    instance.interceptors.request.use(addAuthAndLog);
    instance.interceptors.response.use(logResponse, logError);
    const response = await instance.post<ApiResponse<Contract>>(
        getApiUrl() + API_ENDPOINTS.CONSULT_CONTRACT,
        { ...searchParams, CodCompany: DEFAULT_COMPANY }
    );
    return response.data;
};

export const getAgreementMilestones = async () => {
    const instance = axios.create();
    instance.interceptors.request.use(addAuthAndLog);
    instance.interceptors.response.use(logResponse, logError);
    const response = await instance.post<ApiResponse<AgreementMilestone[]>>(
        getApiUrl() + API_ENDPOINTS.GET_MILESTONES,
        { CodCompany: DEFAULT_COMPANY }
    );
    return response.data;
};

export const updateContract = async (contractData: ContractUpdate) => {
    const instance = axios.create();
    instance.interceptors.request.use(addAuthAndLog);
    instance.interceptors.response.use(logResponse, logError);
    const response = await instance.post<ApiResponse<any>>(
        getApiUrl() + API_ENDPOINTS.UPDATE_CONTRACT,
        { ...contractData, CodCompany: DEFAULT_COMPANY, Send: true }
    );
    return response.data;
};

// Interfaz para los parámetros de búsqueda
interface SearchParams {
    CodCompany: string;
    CodContract?: string;
    CodSalesQuote?: string;
    Version?: string;
}

// Interfaz para la respuesta del contrato
interface ContractResponse {
    CodContract: string;
    ContractDate: string;
    ContractAmount: number;
    TermsOfPayments: Array<{
        IDAgreementMilestone: number;
        Percent: number;
        Date: string;
        Description: string;
    }>;
}

export const searchContract = async (params: SearchParams): Promise<ContractResponse> => {
    const response = await axios.post(getApiUrl() + API_ENDPOINTS.CONSULT_CONTRACT, params);
    return response.data;
};

export const getMilestones = async () => {
    try {
        const response = await fetch(`${getApiUrl()}${API_ENDPOINTS.GET_MILESTONES}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (!response.ok) {
            throw new Error('Error al obtener los hitos');
        }

        const data = await response.json();
        return {
            success: true,
            data: data
        };
    } catch (error: any) {
        console.error('Error en getMilestones:', error);
        return {
            success: false,
            data: error.message || 'Error al obtener los hitos'
        };
    }
}; 

export const getUserRPS = async (searchParams: User) => {
    const instance = axios.create();
    instance.interceptors.request.use(addAuthAndLog);
    instance.interceptors.response.use(logResponse, logError);
    const response = await instance.post<ApiResponse<User>>(
        getApiUrl() + API_ENDPOINTS.LOGINUSER,
        { ...searchParams }
    );
    return response.data;
};

export const getContractsList = async (codCompany: string, codUser: string) => {
    const instance = axios.create();
    instance.interceptors.request.use(addAuthAndLog);
    instance.interceptors.response.use(logResponse, logError);
    const response = await instance.post<ApiResponse<Contract>>(
        getApiUrl() + API_ENDPOINTS.GET_CONTRACTS_LIST,
        { CodCompany: codCompany, CodUser: codUser }
    );
    return response.data;  
};
